import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  // Check for merchant impersonation token first (for cabinet pages)
  const urlPath = window.location.pathname;
  const isImpersonating = urlPath.includes('/cabinet') && localStorage.getItem('impersonate_token');
  
  const token = isImpersonating 
    ? localStorage.getItem('impersonate_token') 
    : localStorage.getItem('token');
    
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Skip redirect for login endpoint - let the component handle the error
      const requestUrl = error.config?.url || '';
      if (!requestUrl.includes('/auth/login')) {
        // Clear impersonation if on cabinet
        if (window.location.pathname.includes('/cabinet')) {
          localStorage.removeItem('impersonate_token');
          localStorage.removeItem('impersonate_merchant');
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  getRoles: () => api.get('/auth/roles'),
};

// Users API
export const usersAPI = {
  list: (page = 1, pageSize = 50) => api.get(`/users?page=${page}&page_size=${pageSize}`),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Payments API
export const paymentsAPI = {
  dashboard: () => api.get('/payments/dashboard'),
  search: (params) => api.get('/payments/search', { params }),
  get: (id) => api.get(`/payments/${id}`),
  list: (page = 1, pageSize = 50) => api.get(`/payments?page=${page}&page_size=${pageSize}`),
  refund: (paymentId, amount, reason) => api.post(`/payments/${paymentId}/refund`, null, { params: { amount, reason } }),
};

// Merchants API
export const merchantsAPI = {
  list: (params) => api.get('/merchants', { params }),
  get: (id) => api.get(`/merchants/${id}`),
  create: (data) => api.post('/merchants', data),
  update: (id, data) => api.put(`/merchants/${id}`, data),
  delete: (id) => api.delete(`/merchants/${id}`),
  createPaymentLink: (data) => api.post('/merchants/payment-link', data),
  refund: (data) => api.post('/merchants/refund', data),
};

// Providers API
export const providersAPI = {
  list: (params) => api.get('/providers', { params }),
  get: (id) => api.get(`/providers/${id}`),
  create: (data) => api.post('/providers', data),
  update: (id, data) => api.put(`/providers/${id}`, data),
  delete: (id) => api.delete(`/providers/${id}`),
};

// Terminals API
export const terminalsAPI = {
  list: (params) => api.get('/terminals', { params }),
  get: (id) => api.get(`/terminals/${id}`),
  create: (data) => api.post('/terminals', data),
  update: (id, data) => api.put(`/terminals/${id}`, data),
  delete: (id) => api.delete(`/terminals/${id}`),
  getCurrencies: () => api.get('/terminals/currencies'),
  getForMerchant: (merchantId) => api.get(`/terminals/merchant/${merchantId}`),
  regenerateKeys: (id) => api.post(`/terminals/${id}/regenerate-keys`),
};

// Commissions API
export const commissionsAPI = {
  stats: () => api.get('/commissions/stats'),
  list: (params) => api.get('/commissions', { params }),
  get: (id) => api.get(`/commissions/${id}`),
  create: (data) => api.post('/commissions', data),
  update: (id, data) => api.put(`/commissions/${id}`, data),
  delete: (id) => api.delete(`/commissions/${id}`),
};

export default api;

// Payment Routing API
export const routingAPI = {
  getProviders: () => api.get('/routing/providers'),
  getMerchantRouting: (merchantId) => api.get(`/routing/merchants/${merchantId}`),
  updateMerchantRouting: (merchantId, data) => api.put(`/routing/merchants/${merchantId}`, data),
  addRule: (merchantId, rule) => api.post(`/routing/merchants/${merchantId}/rules`, rule),
  deleteRule: (merchantId, ruleId) => api.delete(`/routing/merchants/${merchantId}/rules/${ruleId}`),
  checkCard: (merchantId, cardNumber) => api.post(`/routing/merchants/${merchantId}/check-card`, { card_number: cardNumber }),
};

// Test Credentials API
export const testCredentialsAPI = {
  getCards: () => api.get('/test-credentials/cards'),
  getSBP: () => api.get('/test-credentials/sbp'),
  getMerchantConfig: (merchantId) => api.get(`/test-credentials/merchants/${merchantId}`),
  toggleTestMode: (merchantId, enabled) => api.put(`/test-credentials/merchants/${merchantId}/test-mode?enabled=${enabled}`),
};
